import React from "react";

/**
 * AI recommendation badge.
 *
 * Two usage modes:
 *   1. Backend-provided recommendation (candidate report header):
 *      <RecommendationBadge level={rec.level} confidence={rec.confidence}
 *                            reasoning={rec.reasoning} size="md" showConfidence />
 *   2. Client-side derive from a score (tables / cards where only score is loaded):
 *      <RecommendationBadge score={candidate.score} size="sm" />
 */

const STYLES = {
  STRONG_HIRE: {
    label: "✦ Strong hire",
    cls: "bg-emerald-100 text-emerald-700 border-emerald-300",
  },
  HIRE: {
    label: "✓ Hire",
    cls: "bg-vf-tint text-vf-primary border-vf-highlight",
  },
  MAYBE: {
    label: "? Maybe",
    cls: "bg-amber-100 text-amber-700 border-amber-300",
  },
  NO_HIRE: {
    label: "✗ No hire",
    cls: "bg-red-100 text-red-700 border-red-300",
  },
};

const SIZE_CLASSES = {
  sm: "text-[10px] px-1.5 py-0.5 rounded",
  md: "text-xs px-2 py-0.5 rounded",
  lg: "text-sm px-3 py-1 rounded-md",
};

/** Derive a level from a 0–100 score using the plan-doc thresholds. */
export function deriveLevel(score) {
  if (score == null || isNaN(Number(score))) return null;
  const s = Number(score);
  if (s >= 85) return "STRONG_HIRE";
  if (s >= 70) return "HIRE";
  if (s >= 55) return "MAYBE";
  return "NO_HIRE";
}

export default function RecommendationBadge({
  level,
  confidence,
  reasoning,
  score,
  size = "md",
  showConfidence = false,
  className = "",
}) {
  const resolvedLevel = level || deriveLevel(score);
  if (!resolvedLevel || !STYLES[resolvedLevel]) return null;

  const { label, cls } = STYLES[resolvedLevel];
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const confPct =
    confidence != null && !isNaN(Number(confidence))
      ? Math.round(Number(confidence) * 100)
      : null;

  return (
    <span
      className={`inline-flex items-center font-semibold border whitespace-nowrap ${cls} ${sizeCls} ${className}`}
      title={reasoning || undefined}
    >
      {label}
      {showConfidence && confPct != null && (
        <span className="ml-1 opacity-70 font-medium">· {confPct}%</span>
      )}
    </span>
  );
}
